import jsPDF from 'jspdf'
import { asBlob } from 'html-docx-js-typescript'
import { saveAs } from 'file-saver'
import {
  contactDetailLineParts,
  contactFullName,
  WorkExperienceCompany,
  Education,
  SkillCategory,
  Certification,
  Project,
  ProfessionalSummaryEntry,
  ContactInfo,
  ResumeSectionType,
} from '@applicator/shared'

import { getSelectedTargetTitle } from './targetTitleUtils'
import { exportResumeToPDFTwoColumn } from './resumeExportTwoColumn'
import type { ResumeTemplateId } from '../types/resumeTemplate'
import type { ResolvedResumeColors } from '../types/resumeColorScheme'
import { resolveResumeColors, loadResumeColorPreference } from '../types/resumeColorScheme'
import {
  PDF_CONFIG,
  PT_TO_MM,
  TWO_COLUMN_DIVIDER_TO_ROW_MM,
} from './resumeExportPdfConfig'
import { drawResumePdfHeader } from './resumeExportPdfHeader'
import {
  formatCertificationDates,
  isCertificationDatesInPreview,
  isCertificationInPreview,
  isCertificationIssuerInPreview,
  isCertificationNameInPreview,
} from './certificationPreview'
import {
  workExperienceTealLine1HasContent,
  workExperienceTealLine1Parts,
  workExperienceTealLine2HasContent,
  workExperienceTealLine2Parts,
} from './workExperienceRoleHeaderPreview'
import {
  drawTealInlinePartsOnPdf,
  tealLine1DrawStyle,
  tealLine2DrawStyleClassic,
  type TealInlineDrawPart,
  type TealInlineDrawStyle,
} from './resumeExportTealInlineDraw'
import {
  isWorkExperienceCompanyInPreview,
  isWorkExperienceRoleInPreview,
} from './workExperiencePreview'

function exportColors(colors?: ResolvedResumeColors): ResolvedResumeColors {
  return colors ?? resolveResumeColors(loadResumeColorPreference())
}

interface WorkExperienceRole {
  id: string
  position?: string
  positionIncluded?: boolean
  employmentType?: string
  employmentTypeIncluded?: boolean
  location?: string
  locationIncluded?: boolean
  startDate?: string
  endDate?: string
  bulletPoints?: Array<{ id: string; text: string; included: boolean }>
  included?: boolean
}

/**
 * PDF Generator class for creating text-based, ATS-readable PDFs
 * Uses jsPDF to render actual text glyphs (not images)
 * Matches reference "Product Specialist / Business PO" typography
 */
class TextPDFGenerator {
  private pdf: jsPDF
  private y: number
  private contentWidth: number
  private marginLeft: number
  private marginRight: number
  private marginTopMm: number
  private marginBottomMm: number
  private pageNumber: number

  constructor() {
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })
    
    // Convert pt margins to mm
    this.marginLeft = PDF_CONFIG.marginPt.left * PT_TO_MM
    this.marginRight = PDF_CONFIG.marginPt.right * PT_TO_MM
    this.marginTopMm = PDF_CONFIG.marginPt.top * PT_TO_MM
    this.marginBottomMm = PDF_CONFIG.marginPt.bottom * PT_TO_MM
    
    this.y = this.marginTopMm
    this.contentWidth = PDF_CONFIG.pageWidth - this.marginLeft - this.marginRight
    this.pageNumber = 1
    
    // Set default font (helvetica as Roboto substitute)
    this.pdf.setFont('helvetica', 'normal')
    this.activatePage()
  }

  /**
   * Convert pt to mm
   */
  private ptToMm(pt: number): number {
    return pt * PT_TO_MM
  }

  /** Keep jsPDF active page aligned with our page counter (required for reliable multipage export). */
  private activatePage(): void {
    this.pdf.setPage(this.pageNumber)
  }

  private pageContentBottomY(): number {
    return PDF_CONFIG.pageHeight - this.marginBottomMm
  }

  /**
   * Check if we need a new page and add one if necessary
   */
  private checkPageBreak(requiredHeight: number): void {
    const limit = this.pageContentBottomY()
    while (this.y + requiredHeight > limit) {
      this.pdf.addPage()
      this.pageNumber = this.pdf.getNumberOfPages()
      this.y = this.marginTopMm
    }
    this.activatePage()
  }

  /**
   * Set font with size and style
   */
  private setFont(style: 'normal' | 'bold' = 'normal', sizePt: number = PDF_CONFIG.fontSize.body): void {
    this.pdf.setFont('helvetica', style)
    this.pdf.setFontSize(sizePt)
  }

  /**
   * Set text color
   */
  private setColor(color: [number, number, number]): void {
    this.pdf.setTextColor(color[0], color[1], color[2])
  }

  /**
   * Normalize exported text to avoid artificial ATS splits:
   * - collapse embedded new lines/tabs into spaces
   * - remove soft hyphen artifacts and duplicated bullet glyphs
   * - trim duplicated spaces
   */
  private normalizeExportText(text: string): string {
    return String(text || '')
      .replace(/\u00ad/g, '') // soft hyphen
      .replace(/[\u2022\u25CF\uF0B7]/g, ' ') // bullet-like glyphs from imported content
      .replace(/\s+/g, ' ')
      .trim()
  }

  /**
   * Calculate text height for given text and width
   */
  private getTextHeight(text: string, sizePt: number, maxWidthMm: number): number {
    this.setFont('normal', sizePt)
    const lines = this.pdf.splitTextToSize(text, maxWidthMm)
    return lines.length * this.ptToMm(sizePt) * PDF_CONFIG.lineHeight.body
  }

  /**
   * Name + wrapped contact + divider (shared with two-column PDF).
   */
  renderHeader(fullName: string, contactParts: string[], nameColor: [number, number, number]): void {
    drawResumePdfHeader(
      {
        pdf: this.pdf,
        leftX: this.marginLeft,
        fullContentW: this.contentWidth,
        getY: () => this.y,
        setY: (y) => {
          this.y = y
        },
        checkPageBreak: (h) => this.checkPageBreak(h),
        ptToMm: (pt) => this.ptToMm(pt),
        normalizeText: (t) => this.normalizeExportText(t),
        beforeDraw: () => this.activatePage(),
      },
      {
        fullName,
        contactParts,
        nameColor,
        afterDividerGapMm: TWO_COLUMN_DIVIDER_TO_ROW_MM,
      }
    )
  }

  /**
   * Add section header with divider
   * 
   * Visual layout (equal small gaps above and below divider):
   * 
   *   WORK EXPERIENCE          <- header text (baseline)
   *        │ 3pt gap
   *   ─────┼─────────────      <- divider line
   *        │ 3pt gap  
   *   Job Title...             <- first content (top of ascender)
   * 
   * The gaps are measured visually:
   * - FROM bottom of header text TO divider = gapToLine
   * - FROM divider TO top of content text = gapToContent
   */
  addSectionHeader(title: string, headerColor: [number, number, number] = PDF_CONFIG.colors.accent): void {
    const cfg = PDF_CONFIG.sectionHeader
    
    // Add margin-top (24pt before section)
    this.addSpace(this.ptToMm(cfg.marginTop))
    
    // Set font: 10pt (Roboto Light approximation)
    this.setFont('normal', PDF_CONFIG.fontSize.sectionHeader)
    this.setColor(headerColor)
    
    // Font metrics
    const headerFontSize = PDF_CONFIG.fontSize.sectionHeader // 10pt
    const headerDescender = this.ptToMm(headerFontSize * 0.2) // ~2pt descender
    
    const nextContentFontSize = PDF_CONFIG.fontSize.jobTitle // 12pt
    const nextContentAscender = this.ptToMm(nextContentFontSize * 0.8) // ~9.6pt
    
    // Gaps (equal and small)
    const gapToLineMm = this.ptToMm(cfg.gapToLine) // 3pt
    const lineThicknessMm = this.ptToMm(cfg.lineThickness) // 0.75pt
    const gapToContentMm = this.ptToMm(cfg.gapToContent) // 3pt
    
    // Total height for page break check
    const totalHeight = headerDescender + gapToLineMm + lineThicknessMm + gapToContentMm + nextContentAscender
    this.checkPageBreak(totalHeight + this.ptToMm(headerFontSize))
    
    // No letter-spacing for ATS readability
    this.pdf.setCharSpace(0)
    
    // === Draw section header text ===
    // pdf.text() draws from baseline, text extends up (ascender) and down (descender)
    this.pdf.text(title.toUpperCase(), this.marginLeft, this.y)
    
    // === Move Y to bottom of header text ===
    // Only add descender height (not full line-height) for tighter spacing
    this.y += headerDescender
    
    // === Add gap from header bottom to divider ===
    this.y += gapToLineMm // 3pt
    
    // === Draw divider line ===
    this.pdf.setDrawColor(0, 0, 0) // Black #000000
    this.pdf.setLineWidth(lineThicknessMm) // 0.75pt
    this.pdf.line(
      this.marginLeft, 
      this.y, 
      PDF_CONFIG.pageWidth - this.marginRight, 
      this.y
    )
    
    // === Position Y for next content ===
    // We want: gap from divider to TOP of next text (ascender) = gapToContent
    // Next text baseline will be at: divider + gapToContent + ascender
    this.y += lineThicknessMm / 2  // Move past divider thickness
    this.y += gapToContentMm       // 3pt gap to top of next text
    this.y += nextContentAscender  // Offset for ascender so baseline is correct
    
    // Now: gap above divider ≈ gap below divider (visually equal)
  }

  /**
   * Flowing Teal-style inline segments (classic single-column layout).
   */
  private drawTealInlineParts(
    parts: ReadonlyArray<TealInlineDrawPart>,
    style: TealInlineDrawStyle
  ): void {
    this.setColor(PDF_CONFIG.colors.black)
    drawTealInlinePartsOnPdf(
      this.pdf,
      {
        leftX: () => this.marginLeft,
        maxX: this.marginLeft + this.contentWidth,
        getY: () => this.y,
        setY: (y) => {
          this.y = y
        },
        checkPageBreak: (lineH) => this.checkPageBreak(lineH),
        onBeforeLine: () => this.activatePage(),
      },
      parts,
      style,
      PDF_CONFIG.colors.black
    )
  }

  /** Classic PDF line 2: Position • Employment • Dates (inline). */
  private addTealRoleLine2Classic(
    parts: ReturnType<typeof workExperienceTealLine2Parts>
  ): void {
    if (!workExperienceTealLine2HasContent(parts)) return
    this.drawTealInlineParts(parts, tealLine2DrawStyleClassic((pt) => this.ptToMm(pt)))
  }

  /** Classic PDF line 1: Company • Location • Dates (inline, first role per company). */
  private addTealRoleLine1Classic(
    parts: ReturnType<typeof workExperienceTealLine1Parts>
  ): void {
    if (!workExperienceTealLine1HasContent(parts)) return
    this.drawTealInlineParts(parts, tealLine1DrawStyle((pt) => this.ptToMm(pt)))
  }

  /**
   * Skills block in up to 3 columns (Paysafe / Teal PDF style)
   */
  addSkillsColumns(categories: SkillCategory[]): void {
    const active = categories
      .map((cat) => {
        const skills = cat.skills.filter((s) => s.included).map((s) => s.name).join(', ')
        return skills ? { name: cat.name, skills } : null
      })
      .filter((x): x is { name: string; skills: string } => x !== null)

    if (active.length === 0) return

    const colCount = 3
    const colGapMm = 4
    const colWidth = (this.contentWidth - colGapMm * (colCount - 1)) / colCount
    const cols: Array<Array<{ name: string; skills: string }>> = [[], [], []]
    active.forEach((item, i) => {
      cols[i % colCount].push(item)
    })

    const lineHeight = this.ptToMm(PDF_CONFIG.fontSize.body) * PDF_CONFIG.lineHeight.body
    const startY = this.y
    const colHeights: number[] = []

    for (let c = 0; c < colCount; c++) {
      const x = this.marginLeft + c * (colWidth + colGapMm)
      this.y = startY
      for (const block of cols[c]) {
        this.setFont('bold', PDF_CONFIG.fontSize.body)
        this.setColor(PDF_CONFIG.colors.black)
        const title = `${block.name}:`
        const titleLines = this.pdf.splitTextToSize(title, colWidth)
        for (const tl of titleLines) {
          this.checkPageBreak(lineHeight)
          this.pdf.text(tl, x, this.y)
          this.y += lineHeight
        }
        this.setFont('normal', PDF_CONFIG.fontSize.body)
        const bodyLines = this.pdf.splitTextToSize(block.skills, colWidth)
        for (const bl of bodyLines) {
          this.checkPageBreak(lineHeight)
          this.pdf.text(bl, x, this.y)
          this.y += lineHeight
        }
        this.y += this.ptToMm(4)
      }
      colHeights.push(this.y - startY)
    }

    this.y = startY + Math.max(...colHeights, lineHeight)
  }

  /**
   * Add company name with location
   */
  addCompanyLine(company: string, location: string): void {
    this.setColor(PDF_CONFIG.colors.black)
    const lineHeight = this.ptToMm(PDF_CONFIG.fontSize.company) * PDF_CONFIG.lineHeight.body
    this.checkPageBreak(lineHeight)
    
    let currentX = this.marginLeft
    
    // Company (bold)
    if (company) {
      this.setFont('bold', PDF_CONFIG.fontSize.company)
      this.pdf.text(company, currentX, this.y)
      currentX += this.pdf.getTextWidth(company)
    }
    
    // Location (normal)
    if (location) {
      this.setFont('normal', PDF_CONFIG.fontSize.company)
      const locText = company ? ` • ${location}` : location
      this.pdf.text(locText, currentX, this.y)
    }
    
    this.y += lineHeight
  }

  /**
   * Add paragraph text
   */
  addParagraph(text: string, indent: number = 0): void {
    const normalizedText = this.normalizeExportText(text)
    if (!normalizedText) return

    this.setFont('normal', PDF_CONFIG.fontSize.body)
    this.setColor(PDF_CONFIG.colors.black)
    
    const maxWidth = this.contentWidth - indent
    const lines = this.pdf.splitTextToSize(normalizedText, maxWidth)
    const lineHeight = this.ptToMm(PDF_CONFIG.fontSize.body) * PDF_CONFIG.lineHeight.body
    
    for (const line of lines) {
      this.checkPageBreak(lineHeight)
      this.pdf.text(line, this.marginLeft + indent, this.y)
      this.y += lineHeight
    }
  }

  /**
   * Add bullet point
   */
  addBullet(text: string): void {
    const normalizedText = this.normalizeExportText(text)
    if (!normalizedText) return

    this.setFont('normal', PDF_CONFIG.fontSize.bullet)
    this.setColor(PDF_CONFIG.colors.black)
    
    const indent = PDF_CONFIG.spacing.bulletIndent
    const bulletX = this.marginLeft + indent - 3
    const textX = this.marginLeft + indent
    const maxWidth = this.contentWidth - indent
    
    const lines = this.pdf.splitTextToSize(normalizedText, maxWidth)
    const lineHeight = this.ptToMm(PDF_CONFIG.fontSize.bullet) * PDF_CONFIG.lineHeight.bullet
    
    // First line with bullet
    this.checkPageBreak(lineHeight)
    this.pdf.text('•', bulletX, this.y)
    this.pdf.text(lines[0], textX, this.y)
    this.y += lineHeight
    
    // Remaining lines (indented, no bullet)
    for (let i = 1; i < lines.length; i++) {
      this.checkPageBreak(lineHeight)
      this.pdf.text(lines[i], textX, this.y)
      this.y += lineHeight
    }
  }

  /**
   * Add vertical space (checks page break so following sections stay visible).
   */
  addSpace(mm: number): void {
    if (mm > 0) {
      this.checkPageBreak(mm)
    }
    this.y += mm
  }

  /**
   * Add text with right-aligned date
   */
  addTextWithDate(leftText: string, rightText: string, bold: boolean = false): void {
    this.setColor(PDF_CONFIG.colors.black)
    const lineHeight = this.ptToMm(PDF_CONFIG.fontSize.body) * PDF_CONFIG.lineHeight.body
    this.checkPageBreak(lineHeight)
    
    // Right text position
    this.setFont('normal', PDF_CONFIG.fontSize.body)
    const rightWidth = rightText ? this.pdf.getTextWidth(rightText) : 0
    const rightX = PDF_CONFIG.pageWidth - this.marginRight - rightWidth
    
    // Left text
    this.setFont(bold ? 'bold' : 'normal', PDF_CONFIG.fontSize.body)
    this.pdf.text(leftText, this.marginLeft, this.y)
    
    // Right text
    if (rightText) {
      this.setFont('normal', PDF_CONFIG.fontSize.body)
      this.pdf.text(rightText, rightX, this.y)
    }
    
    this.y += lineHeight
  }

  /**
   * Classic single-column body: all preview sections after header.
   */
  renderClassicBody(
    data: Record<ResumeSectionType, Record<string, unknown>>,
    sectionHeaderColor: [number, number, number]
  ): void {
    const selectedTargetTitle = getSelectedTargetTitle(data.target_title)
    if (selectedTargetTitle) {
      this.addSpace(4)
      this.addParagraph(selectedTargetTitle)
    }

    if (data.professional_summary && Array.isArray(data.professional_summary)) {
      const summaries = (data.professional_summary as ProfessionalSummaryEntry[]).filter(
        (entry) => entry.included
      )

      if (summaries.length > 0) {
        this.addSpace(4)
        for (const entry of summaries) {
          this.addParagraph(entry.text)
          this.addSpace(2)
        }
      }
    }

    if (data.work_experience && Array.isArray(data.work_experience)) {
      const companies = (data.work_experience as WorkExperienceCompany[]).filter((company) =>
        isWorkExperienceCompanyInPreview(company)
      )

      if (companies.length > 0) {
        this.addSectionHeader('Work Experience', sectionHeaderColor)

        for (const company of companies) {
          const roles = (company.roles || []).filter((role: WorkExperienceRole) =>
            isWorkExperienceRoleInPreview(company, role)
          )

          for (const role of roles as WorkExperienceRole[]) {
            const line2Parts = workExperienceTealLine2Parts(role)
            if (workExperienceTealLine2HasContent(line2Parts)) {
              this.addTealRoleLine2Classic(line2Parts)
            }

            const line1Parts = workExperienceTealLine1Parts(company, role)
            if (workExperienceTealLine1HasContent(line1Parts)) {
              this.addTealRoleLine1Classic(line1Parts)
            }

            if ((company.descriptionIncluded ?? true) && company.description) {
              this.addSpace(1)
              this.addParagraph(company.description)
            }

            const bullets = (role.bulletPoints || []).filter((bp) => bp.included)
            if (bullets.length > 0) {
              this.addSpace(1)
              for (const bp of bullets) {
                this.addBullet(bp.text)
              }
            }

            this.addSpace(PDF_CONFIG.spacing.itemGap)
          }
        }
      }
    }

    if (data.education && Array.isArray(data.education)) {
      const educations = (data.education as Education[]).filter((edu) => edu.included ?? false)

      if (educations.length > 0) {
        this.addSectionHeader('Education', sectionHeaderColor)

        for (const edu of educations) {
          if ((edu.degreeIncluded ?? false) && edu.degree) {
            const degreeText = edu.fieldOfStudy
              ? `${edu.degree} in ${edu.fieldOfStudy}`
              : edu.degree
            this.addTextWithDate(degreeText, '', true)
          }

          const institution = edu.institution || ''
          const location = (edu.locationIncluded ?? false) && edu.location ? edu.location : ''
          const institutionText = location ? `${institution} • ${location}` : institution

          const startDate = (edu.startDateIncluded ?? false) && edu.startDate ? edu.startDate : ''
          const endDate = (edu.endDateIncluded ?? false) && edu.endDate ? edu.endDate : ''
          const dates = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate

          this.addTextWithDate(institutionText, dates, true)

          if ((edu.additionalInformationIncluded ?? false) && edu.additionalInformation) {
            this.addSpace(1)
            this.addParagraph(edu.additionalInformation)
          }

          this.addSpace(PDF_CONFIG.spacing.itemGap)
        }
      }
    }

    if (data.skills && Array.isArray(data.skills) && data.skills.length > 0) {
      const hasSkills = (data.skills as SkillCategory[]).some((cat) =>
        cat.skills.some((s) => s.included)
      )

      if (hasSkills) {
        this.addSectionHeader('Skills', sectionHeaderColor)

        for (const category of data.skills as SkillCategory[]) {
          const skills = category.skills
            .filter((s) => s.included)
            .map((s) => s.name)
            .join(', ')
          if (skills) {
            this.addTextWithDate(`${category.name}:`, '', true)
            this.addParagraph(skills)
            this.addSpace(2)
          }
        }
      }
    }

    if (data.interests && Array.isArray(data.interests) && data.interests.length > 0) {
      const interests = (data.interests as Array<{ name: string; included?: boolean }>)
        .filter((i) => i.included !== false)
        .map((i) => i.name)
        .join(', ')

      if (interests) {
        this.addSectionHeader('Interests', sectionHeaderColor)
        this.addParagraph(interests)
      }
    }

    if (data.certifications && Array.isArray(data.certifications)) {
      const certs = (data.certifications as Certification[]).filter(isCertificationInPreview)

      if (certs.length > 0) {
        this.addSectionHeader('Certifications', sectionHeaderColor)

        for (const cert of certs) {
          const dates = isCertificationDatesInPreview(cert) ? formatCertificationDates(cert) : ''
          const title = isCertificationNameInPreview(cert) ? cert.name : ''

          if (title || dates) {
            this.addTextWithDate(title, dates, true)
          }

          if (isCertificationIssuerInPreview(cert)) {
            this.addParagraph(cert.issuer!)
          }

          this.addSpace(PDF_CONFIG.spacing.itemGap)
        }
      }
    }

    if (data.projects && Array.isArray(data.projects)) {
      const projects = (data.projects as Project[]).filter((project) => project.included)

      if (projects.length > 0) {
        this.addSectionHeader('Projects', sectionHeaderColor)

        for (const project of projects) {
          const dates =
            project.startDate && project.endDate
              ? `${project.startDate} - ${project.endDate}`
              : ''

          this.addTextWithDate(project.name, dates, true)

          if (project.company) {
            this.addParagraph(project.company)
          }

          const bullets = (project.bulletPoints || []).filter((bp) => bp.included)
          if (bullets.length > 0) {
            this.addSpace(1)
            for (const bp of bullets) {
              this.addBullet(bp.text)
            }
          }

          this.addSpace(PDF_CONFIG.spacing.itemGap)
        }
      }
    }
  }

  /**
   * Save the PDF
   */
  save(filename: string): void {
    this.pdf.save(filename)
  }

  /**
   * Get current Y position
   */
  getY(): number {
    return this.y
  }
}

/**
 * Export resume to ATS-readable PDF with real selectable text
 */
export async function exportResumeToPDF(
  _element: HTMLElement,
  filename: string = 'resume.pdf',
  data?: Record<ResumeSectionType, Record<string, unknown>>,
  template: ResumeTemplateId = 'classic',
  colors?: ResolvedResumeColors
): Promise<void> {
  if (!data) {
    // Fallback to print dialog if no data provided
    window.print()
    return
  }

  const resolvedColors = exportColors(colors)

  if (template === 'two_column') {
    await exportResumeToPDFTwoColumn(data, filename, resolvedColors)
    return
  }

  const pdf = new TextPDFGenerator()
  const nameColor = resolvedColors.accentRgb
  const sectionHeaderColor = resolvedColors.accentRgb

  // === HEADER: Name and Contact (same wrap rules as two-column PDF) ===
  const contactInfo = data.contact_info as ContactInfo
  const fullName = contactFullName(contactInfo) || 'Your Name'
  const contactParts = contactDetailLineParts(contactInfo)

  pdf.renderHeader(fullName, contactParts, nameColor)
  pdf.renderClassicBody(data, sectionHeaderColor)

  // Save the PDF
  pdf.save(filename)
}

/**
 * Trigger browser print dialog for ATS-compatible PDF
 * Uses the browser's native print functionality
 */
export function printResume(): void {
  window.print()
}

/**
 * Export resume to DOCX format
 */
export async function exportResumeToDOCX(
  element: HTMLElement, 
  filename: string = 'resume.docx',
  template: ResumeTemplateId = 'classic',
  colors?: ResolvedResumeColors
): Promise<void> {
  try {
    const resolvedColors = exportColors(colors)
    const clonedElement = element.cloneNode(true) as HTMLElement
    clonedElement.setAttribute('data-resume-template', template)

    // Remove interactive elements
    const interactiveElements = clonedElement.querySelectorAll('[role="button"], button, a[href]')
    interactiveElements.forEach((el) => {
      el.removeAttribute('role')
      el.removeAttribute('tabindex')
      el.removeAttribute('onclick')
      el.removeAttribute('onkeydown')
      if (el instanceof HTMLElement) {
        el.style.cursor = 'default'
      }
    })

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 48px;
              color: #000000;
            }
            h1 { font-size: 22pt; font-weight: bold; text-align: center; margin-bottom: 8px; }
            h1.template-classic, [data-resume-template="classic"] h1 { color: ${resolvedColors.nameHex}; }
            h1.template-paysafe, h1.template-two_column, [data-resume-template="two-column"] h1, [data-resume-template="two_column"] h1 { color: ${resolvedColors.nameHex}; }
            .paysafe-header { text-align: center; margin-bottom: 24px; }
            .paysafe-columns { width: 100%; border-collapse: collapse; table-layout: fixed; }
            .paysafe-col-left { vertical-align: top; }
            .paysafe-col-gap { padding: 0; }
            .paysafe-col-right { vertical-align: top; }
            h2.resume-section-header, [data-resume-template="two_column"] h2.resume-section-header, [data-resume-template="classic"] h2.resume-section-header { font-size: 10pt; font-weight: normal; text-transform: uppercase; margin-bottom: 4px; color: ${resolvedColors.sectionHeaderHex}; }
            h2 { font-size: 12pt; font-weight: 600; margin-bottom: 8px; color: #000000; }
            h3 { font-size: 10pt; font-weight: bold; margin-bottom: 4px; color: #000000; }
            p { font-size: 10pt; line-height: 1.17; margin-bottom: 8px; color: #000000; }
            ul { list-style: disc; padding-left: 24px; margin-left: 0; }
            li { font-size: 10pt; line-height: 1.17; margin-bottom: 4px; color: #000000; }
          </style>
        </head>
        <body>
          ${clonedElement.innerHTML}
        </body>
      </html>
    `

    const docxBlob = await asBlob(htmlContent)
    saveAs(docxBlob, filename)
  } catch (error) {
    // Error handled silently
    throw error
  }
}
